import { z } from 'zod';
import {
  cellScanResultSchema,
  cellStatusSchema,
  rackGeometryBase,
  rackKindSchema,
  type CellCardDto,
  type CreateRackInput,
  type CreateWarehouseInput,
  type CreateZoneInput,
  type UpdateCellInput,
  type UpdateRackInput,
  type UpdateWarehouseInput,
  type UpdateZoneInput,
} from '@eombor/contracts';
import { apiClient } from '../../auth/model/auth.store.js';
import type { ApiClient } from '../../../shared/api/client.js';

/**
 * Backend javoblarining ish vaqtidagi sxemasi. Manba:
 *   `warehouse/infrastructure/http/{warehouse,zone,rack,cell}.controller.ts`
 *   `warehouse/domain/warehouse.ts` (`WarehouseRow`, `WarehouseLayout`, …)
 *
 * Turlar `apps/api` dan IMPORT QILINMAYDI (frontend alohida repositoriyaga
 * ko'chadi) — shakl shu yerda qaytadan e'lon qilinadi va Zod har javobda uni
 * tekshirib turadi: shartnoma buzilsa xato aynan shu yerda chiqadi, sahifa
 * o'rtasida `undefined` bo'lib emas.
 *
 * Diqqat: katakning FAZODAGI o'rni javobda YO'Q. U `cellTransform()`
 * (`@eombor/contracts`) bilan stellaj geometriyasidan hisoblanadi.
 */

export const warehouseDimsSchema = z.object({
  lengthM: z.number(),
  widthM: z.number(),
  heightM: z.number(),
});

export const warehouseSchema = z.object({
  id: z.string(),
  mtuId: z.string(),
  orgUnitId: z.string(),
  code: z.string(),
  name: z.string(),
  address: z.string().nullable(),
  dims: warehouseDimsSchema,
  isActive: z.boolean(),
  /** Hovli — tomi va devori yo'q: 3D da qobiq chizilmaydi. */
  isOutdoor: z.boolean(),
});

export type WarehouseDto = z.infer<typeof warehouseSchema>;

export const warehousePageSchema = z.object({
  items: z.array(warehouseSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
});

export type WarehousePage = z.infer<typeof warehousePageSchema>;

export const zonePlanSchema = z.object({
  x: z.number(),
  y: z.number(),
  w: z.number(),
  d: z.number(),
});

export const zoneSchema = z.object({
  id: z.string(),
  warehouseId: z.string(),
  code: z.string(),
  name: z.string(),
  color: z.string(),
  plan: zonePlanSchema,
});

export type ZoneDto = z.infer<typeof zoneSchema>;

/** Stellaj geometriyasi kontraktdan keladi — bu yerda takrorlanmaydi. */
const rackShape = {
  id: z.string(),
  zoneId: z.string(),
  code: z.string(),
  name: z.string().nullable(),
  /** `FLOOR` — ochiq maydon: 3D da temir chizilmaydi, matnlarda «maydon». */
  kind: rackKindSchema,
  pos: rackGeometryBase.shape.pos,
  depthM: rackGeometryBase.shape.depthM,
  bays: rackGeometryBase.shape.bays,
  levelHeights: rackGeometryBase.shape.levelHeights,
};

export const rackSchema = z.object(rackShape);

export type RackDto = z.infer<typeof rackSchema>;

/**
 * Katak ajratilgan mahsulot.
 *
 * Kod ham, nom ham keladi: kartochka nomni ko'rsatadi, kirim satridagi
 * ogohlantirish esa qaysi mahsulot ekanini aniq aytishi kerak.
 */
export const reservedItemSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
});

export type ReservedItem = z.infer<typeof reservedItemSchema>;

/**
 * Katak satri AJRATISHSIZ — stellaj javobi aynan shuni qaytaradi.
 *
 * Ajratish `cell_reservation` jadvalida yotadi va uni o'qish har katak
 * uchun alohida so'rov talab qiladi. Server buni ataylab qilmaydi:
 * stellaj qurilganda yoki qatori bo'lingandagi javob o'nlab katakni
 * qaytaradi va ularning har biriga ajratishni yig'ish qimmat. Shuning
 * uchun `reserved` faqat BITTA katak so'ralganda keladi.
 *
 * Ilgari bu yerda ham `reserved` majburiy edi va natijada stellaj
 * saqlash HAR SAFAR "Serverdan kutilmagan javob keldi" bilan tugardi:
 * server to'g'ri javob berardi, sxema esa uni rad etardi.
 */
const cellShape = {
  id: z.string(),
  rackId: z.string(),
  warehouseId: z.string(),
  code: z.string(),
  level: z.number(),
  idx: z.number(),
  status: cellStatusSchema,
  capacityQty: z.number().nullable(),
  capacityWeightKg: z.number().nullable(),
  note: z.string().nullable(),
};

export const rackCellSchema = z.object(cellShape);

export type RackCell = z.infer<typeof rackCellSchema>;

/** To'liq katak satri — `GET /cells/by-code/:code` va `PATCH /cells/:id` javobi. */
export const cellSchema = z.object({
  ...cellShape,
  /** Katak SHU mahsulotlar uchun ajratilgan. Bo'sh ro'yxat — umumiy katak. */
  reserved: z.array(reservedItemSchema),
});

export type CellDto = z.infer<typeof cellSchema>;

/** Layout dagi katak — chizish uchun kerak bo'lgan maydonlar. */
export const layoutCellSchema = z.object({
  id: z.string(),
  code: z.string(),
  level: z.number(),
  idx: z.number(),
  status: cellStatusSchema,
  fillPercent: z.number(),
  /** Katakdagi ASOSIY mahsulot nomi — fasadda katakning ichida turadi. */
  topItem: z.string().nullable(),
  /** Turli nomenklatura soni — "va yana 2 xil" yozuvi uchun. */
  itemKinds: z.number(),
  /** Katak SHU mahsulotlar uchun ajratilgan. Bo'sh ro'yxat — umumiy katak. */
  reservedFor: z.array(reservedItemSchema),
});

export type LayoutCell = z.infer<typeof layoutCellSchema>;

export const layoutRackSchema = z.object({ ...rackShape, cells: z.array(layoutCellSchema) });

export type LayoutRack = z.infer<typeof layoutRackSchema>;

export const layoutZoneSchema = zoneSchema.extend({ racks: z.array(layoutRackSchema) });

export type LayoutZone = z.infer<typeof layoutZoneSchema>;

export const warehouseLayoutSchema = z.object({
  warehouse: warehouseSchema,
  zones: z.array(layoutZoneSchema),
});

export type LayoutDto = z.infer<typeof warehouseLayoutSchema>;

/** `POST /racks` kataklari bilan qaytadi — etiketka bosish shu ro'yxatdan boshlanadi. */
export const rackNodeSchema = z.object({ ...rackShape, cells: z.array(rackCellSchema) });

export type RackNode = z.infer<typeof rackNodeSchema>;

export interface WarehouseQuery {
  mtuId?: string;
  orgUnitId?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface WarehouseApi {
  list: (query?: WarehouseQuery) => Promise<WarehousePage>;
  getById: (id: string) => Promise<WarehouseDto>;
  layout: (id: string) => Promise<LayoutDto>;
  create: (input: CreateWarehouseInput) => Promise<WarehouseDto>;
  update: (id: string, input: UpdateWarehouseInput) => Promise<WarehouseDto>;
  createZone: (input: CreateZoneInput) => Promise<ZoneDto>;
  updateZone: (id: string, input: UpdateZoneInput) => Promise<ZoneDto>;
  deleteZone: (id: string) => Promise<void>;
  createRack: (input: CreateRackInput) => Promise<RackNode>;
  updateRack: (id: string, input: UpdateRackInput) => Promise<RackNode>;
  deleteRack: (id: string) => Promise<void>;
  /**
   * KATAKLARNI BIRLASHTIRISH — javondagi ajratgich olib tashlandi.
   * Javob butun stellaj: qatorning bo'linishi ham o'zgaradi.
   */
  mergeCells: (cellIds: readonly string[]) => Promise<RackNode>;
  cellByCode: (warehouseId: string, code: string) => Promise<CellDto>;
  /**
   * SKANER. Ombor IXTIYORIY: javon oldida turgan odamning skaneri qaysi
   * omborda ekanini bilmaydi. Javob ro'yxat — kod ikki omborda takrorlanishi
   * mumkin va serverning taxmin qilishiga yo'l qo'yilmaydi.
   */
  scanCell: (code: string, warehouseId?: string) => Promise<CellCardDto[]>;
  updateCell: (id: string, input: UpdateCellInput) => Promise<CellDto>;
}

const queryString = (query: WarehouseQuery): string => {
  const params = new URLSearchParams({
    page: String(query.page ?? 1),
    limit: String(query.limit ?? 60),
  });
  if (query.mtuId !== undefined) params.set('mtuId', query.mtuId);
  if (query.orgUnitId !== undefined) params.set('orgUnitId', query.orgUnitId);
  if (query.search !== undefined && query.search !== '') params.set('search', query.search);
  return params.toString();
};

export const createWarehouseApi = (client: ApiClient): WarehouseApi => ({
  list: (query = {}) =>
    client.apiFetch(`/warehouses?${queryString(query)}`, { schema: warehousePageSchema }),

  getById: (id) => client.apiFetch(`/warehouses/${id}`, { schema: warehouseSchema }),

  layout: (id) => client.apiFetch(`/warehouses/${id}/layout`, { schema: warehouseLayoutSchema }),

  create: (input) =>
    client.apiFetch('/warehouses', {
      method: 'POST',
      body: JSON.stringify(input),
      schema: warehouseSchema,
    }),

  update: (id, input) =>
    client.apiFetch(`/warehouses/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
      schema: warehouseSchema,
    }),

  createZone: (input) =>
    client.apiFetch('/zones', { method: 'POST', body: JSON.stringify(input), schema: zoneSchema }),

  updateZone: (id, input) =>
    client.apiFetch(`/zones/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
      schema: zoneSchema,
    }),

  deleteZone: async (id) => {
    await client.apiFetch(`/zones/${id}`, { method: 'DELETE' });
  },

  createRack: (input) =>
    client.apiFetch('/racks', {
      method: 'POST',
      body: JSON.stringify(input),
      schema: rackNodeSchema,
    }),

  updateRack: (id, input) =>
    client.apiFetch(`/racks/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
      schema: rackNodeSchema,
    }),

  deleteRack: async (id) => {
    await client.apiFetch(`/racks/${id}`, { method: 'DELETE' });
  },

  mergeCells: (cellIds) =>
    client.apiFetch('/cells/merge', {
      method: 'POST',
      body: JSON.stringify({ cellIds: [...cellIds] }),
      schema: rackNodeSchema,
    }),

  /**
   * SKANER YO'LI. Kod manzilga qo'yiladi, shuning uchun `encodeURIComponent`
   * majburiy: skaner qo'shimcha belgi yuborsa ham manzil buzilmaydi.
   */
  cellByCode: (warehouseId, code) =>
    client.apiFetch(
      `/cells/by-code/${encodeURIComponent(code)}?warehouseId=${encodeURIComponent(warehouseId)}`,
      { schema: cellSchema },
    ),

  scanCell: (code, warehouseId) =>
    client
      .apiFetch(
        `/cells/scan/${encodeURIComponent(code)}${
          warehouseId === undefined ? '' : `?warehouseId=${encodeURIComponent(warehouseId)}`
        }`,
        { schema: cellScanResultSchema },
      )
      .then((result) => result.matches),

  updateCell: (id, input) =>
    client.apiFetch(`/cells/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
      schema: cellSchema,
    }),
});

/** Ilovaning yagona klienti ustidagi tayyor ko'rinish. */
export const warehouseApi = createWarehouseApi(apiClient);
